import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useDatabase } from '../context/DatabaseContext';
import { useForm } from 'react-hook-form';
import { Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';

const AddCustomer = () => {
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [documents, setDocuments] = useState([]);
  const { dispatch } = useApp();
  const { addCustomer, updateCustomer, loading } = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();
  
  // Check if we're in edit mode
  const editMode = location.state?.editMode;
  const customerToEdit = location.state?.customer;

  // Populate form when in edit mode
  useEffect(() => {
    if (editMode && customerToEdit) {
      setValue('name', customerToEdit.name || '');
      setValue('fatherName', customerToEdit.fatherName || '');
      setValue('motherName', customerToEdit.motherName || '');
      setValue('mobile', customerToEdit.mobile || '');
      setValue('email', customerToEdit.email || '');
      setValue('address', customerToEdit.address || '');
      
      if (customerToEdit.photo) {
        setCustomerPhoto(customerToEdit.photo);
      }
      
      if (customerToEdit.documents) {
        setDocuments(customerToEdit.documents);
      }
    }
  }, [editMode, customerToEdit, setValue]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setCustomerPhoto(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setDocuments(prev => [...prev, {
          id: Date.now() + Math.random(),
          name: file.name,
          data: e.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (id) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  const onSubmit = (data) => {
    const customerData = {
      ...data,
      photo: customerPhoto,
      documents: documents,
      createdAt: editMode ? customerToEdit.createdAt : new Date().toISOString(),
      id: editMode ? customerToEdit.id : undefined
    };

    if (editMode) {
      dispatch({ type: 'UPDATE_CUSTOMER', payload: customerData });
      toast.success('Customer updated successfully!');
    } else {
      dispatch({ type: 'ADD_CUSTOMER', payload: customerData });
      toast.success('Customer added successfully!');
    }
    
    reset();
    setCustomerPhoto(null);
    setDocuments([]);
    navigate('/customers');
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">
            {editMode ? 'Edit Customer' : 'Add New Customer'}
          </h1>
          <p className="text-gray-600 text-lg">
            {editMode ? 'Update the customer details below' : 'Fill in the customer details below'}
          </p>
        </div>

        <div className="card">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className="input-field"
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Father's Name
              </label>
              <input
                type="text"
                {...register('fatherName')}
                className="input-field"
                placeholder="Enter father's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mother's Name
              </label>
              <input
                type="text"
                {...register('motherName')}
                className="input-field"
                placeholder="Enter mother's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                {...register('mobile', { 
                  required: 'Mobile number is required',
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: 'Please enter a valid 10-digit mobile number'
                  }
                })}
                className="input-field"
                placeholder="Enter mobile number"
              />
              {errors.mobile && (
                <p className="text-red-500 text-sm mt-1">{errors.mobile.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
                className="input-field"
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              {...register('address')}
              rows={3}
              className="input-field"
              placeholder="Enter complete address"
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Photo
            </label>
            <div className="flex items-center space-x-4">
              {customerPhoto ? (
                <div className="relative">
                  <img
                    src={customerPhoto}
                    alt="Customer"
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setCustomerPhoto(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
              />
              <label
                htmlFor="photo-upload"
                className="btn-secondary cursor-pointer"
              >
                Upload Photo
              </label>
            </div>
          </div>

          {/* Documents Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supporting Documents
            </label>
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">{doc.name}</span>
                  <button
                    type="button"
                    onClick={() => removeDocument(doc.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleDocumentUpload}
                className="hidden"
                id="documents-upload"
              />
              <label
                htmlFor="documents-upload"
                className="btn-secondary cursor-pointer inline-block"
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload Documents
              </label>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="btn-primary"
            >
              {editMode ? 'Update Customer' : 'Save Customer'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default AddCustomer;
